import {
  Flex,
  Loader,
  LoadingOverlay as MantineLoadingOverlay,
  Title,
  Text,
  Image,
  Button,
  Alert,
} from '@mantine/core';
import Head from 'next/head';
import { useEffect, useState } from 'react';
import * as Sentry from '@sentry/nextjs';
import { useWeb3Context } from '../providers/Web3Provider/useWeb3Context';
import { IconAlertCircle } from '@tabler/icons-react';

const LoadingOverlay = ({
  overlayId,
  onlyLoader,
}: {
  overlayId?: string | number;
  onlyLoader?: boolean;
}): JSX.Element => {
  const [showResetButton, setShowResetButton] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const web3Context = useWeb3Context();

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setShowResetButton(true);

      // Log to Sentry with web3 context state
      Sentry.captureMessage('Loading overlay timeout - overlay stuck for more than 15 seconds', {
        level: 'error',
        tags: {
          overlayId: overlayId?.toString() || 'unknown',
          onlyLoader: onlyLoader?.toString() || 'false',
        },
        contexts: {
          web3: {
            isConnected: web3Context.isConnected,
            account: web3Context.account,
            currentChain: web3Context.currentChain?.id,
            isExternalWallet: web3Context.isExternalWallet,
            hasPublicClient: !!web3Context.publicClient,
            hasWalletClient: !!web3Context.walletClient,
          },
          browser: {
            url: typeof window !== 'undefined' ? window.location.href : 'unknown',
            userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown',
          },
        },
        extra: {
          web3ContextState: {
            isConnected: web3Context.isConnected,
            account: web3Context.account,
            currentChain: web3Context.currentChain,
            isExternalWallet: web3Context.isExternalWallet,
            publicClient: web3Context.publicClient ? 'available' : 'unavailable',
            walletClient: web3Context.walletClient ? 'available' : 'unavailable',
            envioClient: web3Context.envioClient ? 'available' : 'unavailable',
          },
          cookies: typeof document !== 'undefined' ? document.cookie : 'unavailable',
          localStorage: typeof localStorage !== 'undefined' ? Object.keys(localStorage) : 'unavailable',
          sessionStorage: typeof sessionStorage !== 'undefined' ? Object.keys(sessionStorage) : 'unavailable',
        },
      });
    }, 20000); // 20 seconds timeout for testing

    return () => clearTimeout(timeoutId);
  }, [overlayId, onlyLoader, web3Context]);

  const handleReset = async () => {
    setIsResetting(true);
    try {
      // Clear privy cookies
      if (typeof document !== 'undefined') {
        document.cookie.split(';').forEach((cookie) => {
          const eqPos = cookie.indexOf('=');
          const name = eqPos > -1 ? cookie.substr(0, eqPos).trim() : cookie.trim();
          if (name.includes('privy')) {
            document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
          }
        });
      }

      // Clear localStorage items related to privy and wagmi
      if (typeof localStorage !== 'undefined') {
        Object.keys(localStorage).forEach((key) => {
          if (key.includes('privy') || key.includes('wagmi')) {
            localStorage.removeItem(key);
          }
        });
      }

      // Clear sessionStorage items related to privy
      if (typeof sessionStorage !== 'undefined') {
        Object.keys(sessionStorage).forEach((key) => {
          if (key.includes('privy')) {
            sessionStorage.removeItem(key);
          }
        });
      }

      // Call web3 context logout
      await web3Context.logout();

      // Reload the page to ensure clean state
      if (typeof window !== 'undefined') {
        window.location.reload();
      }
    } catch (error) {
      console.error('Error during reset:', error);
      Sentry.captureException(error, {
        tags: {
          function: 'LoadingOverlayReset',
          overlayId: overlayId?.toString() || 'unknown',
        },
      });
    } finally {
      setIsResetting(false);
    }
  };

  return (
    <>
      <Head>
        <link rel="preload" href="/logo.png" as="image" />
      </Head>
      <MantineLoadingOverlay
        id={overlayId?.toString()}
        visible={true}
        zIndex={1000}
        overlayProps={{ radius: 'sm', blur: 2, backgroundOpacity: 0 }}
        loaderProps={{ children: overlayContent(onlyLoader, showResetButton, handleReset, isResetting) }}
      />
    </>
  );
};

const overlayContent = (
  onlyLoader?: boolean,
  showResetButton?: boolean,
  handleReset?: () => Promise<void>,
  isResetting?: boolean
) => {
  return (
    <Flex direction="column" align="center" justify="center" gap="1rem">
      {!onlyLoader && (
        <>
          <Title ta="center" mt="1rem" fz={{ base: '2rem', sm: '3rem' }} style={{ color: '#DEE2E6' }}>
            Welcome to{' '}
            <Text inherit variant="gradient" component="span" gradient={{ from: 'cyan', to: 'teal' }}>
              Rankify
            </Text>
          </Title>
          <Image src="/logo.png" alt="Logo" maw={{ base: '200px', sm: '320px' }} />
        </>
      )}

      {showResetButton ? (
        <Flex direction="column" align="center" gap="1rem" w="100%" maw="400px">
          <Alert icon={<IconAlertCircle size="1rem" />} title="Loading taking longer than usual" color="orange">
            <Text size="sm" mb="md">
              It looks like loading is taking more time than usual. You can reset the state and try again.
            </Text>
            <Button onClick={handleReset} loading={isResetting} color="orange" fullWidth>
              Reset State & Try Again
            </Button>
          </Alert>
        </Flex>
      ) : (
        <Loader color="teal.9" type="bars" />
      )}
    </Flex>
  );
};

export default LoadingOverlay;
